import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  AUTH_ERROR_CODES,
  authErrorMessage,
  TENANT_API,
  toApiError
} from '@pharvio/core/shared';
import {
  CancelStepUp,
  ClearStepUp,
  RequestStepUpChallenge,
  ResendStepUpCode,
  StepUpChallenge,
  SubmitStepUpCode
} from './step-up.actions';

export type StepUpStatus =
  | 'idle'
  | 'requesting'
  | 'awaiting_code'
  | 'verifying'
  | 'failed';

export interface StepUpStateModel {
  activeChallenge: StepUpChallenge | null;
  status: StepUpStatus;
  attemptsRemaining: number;
  resendAvailableAt: number | null;
  error: string | null;
  queuedRequestCount: number;
  /** Operation the current challenge is for — reused on resend. */
  operation: string | null;
  /**
   * Serialised payload the challenge was issued for. The step-up token is only
   * valid for a request whose body serialises to exactly this string.
   */
  payloadJson: string | null;
  /** Single-use step-up token — consumed by the interceptor */
  token: string | null;
  /**
   * True when the first challenge failed because the user's email address
   * isn't verified — no code can be sent until it is. Read by StepUpService to
   * offer verification instead of a retry.
   */
  needsEmailVerification: boolean;
}

const DEFAULTS: StepUpStateModel = {
  activeChallenge: null,
  status: 'idle',
  attemptsRemaining: 5,
  resendAvailableAt: null,
  error: null,
  queuedRequestCount: 0,
  operation: null,
  payloadJson: null,
  token: null,
  needsEmailVerification: false
};

/** Serialise exactly as HttpClient does for a JSON body. */
export function serialiseStepUpPayload(payload: unknown): string {
  return JSON.stringify(payload ?? null);
}

@State<StepUpStateModel>({
  name: 'stepUp',
  defaults: DEFAULTS
})
@Injectable()
export class StepUpState {
  private readonly http = inject(HttpClient);

  @Selector()
  static activeChallenge(state: StepUpStateModel): StepUpChallenge | null {
    return state.activeChallenge;
  }

  @Selector()
  static status(state: StepUpStateModel): StepUpStatus {
    return state.status;
  }

  @Selector()
  static attemptsRemaining(state: StepUpStateModel): number {
    return state.attemptsRemaining;
  }

  @Selector()
  static resendAvailableAt(state: StepUpStateModel): number | null {
    return state.resendAvailableAt;
  }

  @Selector()
  static error(state: StepUpStateModel): string | null {
    return state.error;
  }

  @Selector()
  static token(state: StepUpStateModel): string | null {
    return state.token;
  }

  @Selector()
  static payloadJson(state: StepUpStateModel): string | null {
    return state.payloadJson;
  }

  @Selector()
  static needsEmailVerification(state: StepUpStateModel): boolean {
    return state.needsEmailVerification;
  }

  @Action(RequestStepUpChallenge)
  requestChallenge(
    ctx: StateContext<StepUpStateModel>,
    { operation, payload }: RequestStepUpChallenge
  ) {
    ctx.patchState({
      status: 'requesting',
      error: null,
      token: null,
      needsEmailVerification: false,
      operation,
      payloadJson: serialiseStepUpPayload(payload)
    });
    return this.issueChallenge(ctx, operation, payload);
  }

  @Action(SubmitStepUpCode)
  submitCode(
    ctx: StateContext<StepUpStateModel>,
    { challengeId, code }: SubmitStepUpCode
  ) {
    ctx.patchState({ status: 'verifying', error: null });
    return this.http
      .post<{
        expiresAt: string;
        stepUpToken: string;
      }>(TENANT_API.STEP_UP.VERIFY, { challengeId, code })
      .pipe(
        tap(({ stepUpToken }) => {
          ctx.patchState({
            status: 'idle',
            token: stepUpToken,
            activeChallenge: null
          });
        }),
        catchError((err) => {
          const apiError = toApiError(err);
          let error =
            "That code isn't right. Check the latest email and try again.";

          if (apiError.code === 'STEP_UP_ATTEMPTS_EXHAUSTED') {
            // Keep activeChallenge: the dialog renders only while one exists,
            // and the user still needs to read this and request a new code.
            ctx.patchState({
              status: 'failed',
              error: 'Too many attempts. Request a new code.'
            });
            return EMPTY;
          }
          if (apiError.code === 'STEP_UP_CODE_EXPIRED') {
            error = 'That code has expired. Request a new one.';
          } else if (apiError.statusCode === 429) {
            error = authErrorMessage(apiError);
          }

          const attempts = ctx.getState().attemptsRemaining - 1;
          ctx.patchState({
            status: 'awaiting_code',
            error,
            attemptsRemaining: Math.max(0, attempts)
          });
          return EMPTY;
        })
      );
  }

  @Action(ResendStepUpCode)
  resendCode(ctx: StateContext<StepUpStateModel>) {
    const { operation, payloadJson } = ctx.getState();
    if (!operation || payloadJson === null) return EMPTY;

    ctx.patchState({ status: 'requesting', error: null, token: null });
    // Re-parse the frozen snapshot so later mutations of the caller's object
    // can't change what the challenge is bound to.
    return this.issueChallenge(ctx, operation, JSON.parse(payloadJson), true);
  }

  @Action(CancelStepUp)
  cancelStepUp(ctx: StateContext<StepUpStateModel>) {
    ctx.setState(DEFAULTS);
  }

  @Action(ClearStepUp)
  clearStepUp(ctx: StateContext<StepUpStateModel>) {
    ctx.setState(DEFAULTS);
  }

  /**
   * @param isResend a resend keeps the current challenge on failure (the dialog
   * is open and renders only while a challenge exists); a first request clears
   * it, which is what tells StepUpService not to open the dialog at all.
   */
  private issueChallenge(
    ctx: StateContext<StepUpStateModel>,
    operation: string,
    payload: unknown,
    isResend = false
  ) {
    return this.http
      .post<StepUpChallenge>(TENANT_API.STEP_UP.CHALLENGE, {
        operation,
        payload
      })
      .pipe(
        tap((challenge) => {
          ctx.patchState({
            activeChallenge: challenge,
            status: 'awaiting_code',
            attemptsRemaining: 5,
            resendAvailableAt: Date.now() + 60_000
          });
        }),
        catchError((err) => {
          const apiError = toApiError(err);
          const needsEmailVerification =
            apiError.code === AUTH_ERROR_CODES.EmailVerificationRequired;

          let error =
            apiError.message ||
            'Could not send a verification code. Please try again.';
          if (needsEmailVerification) {
            error = authErrorMessage(apiError);
          } else if (
            apiError.statusCode === 429 ||
            apiError.code === 'STEP_UP_RATE_LIMITED'
          ) {
            error = 'Too many code requests. Try again in a few minutes.';
          }

          ctx.patchState({
            ...(isResend ? {} : { activeChallenge: null }),
            status: 'failed',
            error,
            needsEmailVerification
          });
          return EMPTY;
        })
      );
  }
}
